import {SPONSOR_ADVERTISING_TERMS_PATH} from "api/endpointPaths.mjs";
import type {
	CheckoutDocumentRead,
	ContractDocumentEndpoints,
	ContractDocumentIdentity,
	LinkedContractDocument,
} from "pay/checkoutDocument";
import {ADVERTISING_TERMS_PATH} from "./sponsorRoutes";

/**
 * The Advertising Terms a booking is submitted under (kitecorp/transferify-api-service#2149), as the
 * booking form handles them. Plain TypeScript — no React — so the two rules below are testable without
 * rendering and survive any rework of the form.
 *
 * The api registers the Advertising Terms exactly as it registers the Terms every checkout is bound to:
 * one immutable document per version and language, announced per brand. The form therefore reuses the
 * checkout's own document selection (`pay/checkoutDocument.ts`), pointed at the sponsor surface, and the
 * submission echoes back the identity of the document the form's link was rendered from — which is what
 * lets the api record, and later attach, the exact bytes the advertiser was shown.
 */

/** Announced and served under one path: `GET` announces, `GET <path>/<version>/<locale>` serves. */
export const ADVERTISING_TERMS_ENDPOINTS: ContractDocumentEndpoints = Object.freeze({
	selection: SPONSOR_ADVERTISING_TERMS_PATH,
	documents: SPONSOR_ADVERTISING_TERMS_PATH,
});

/**
 * The field a submission carries, read from the reader the form rendered its link from.
 *
 * No reader, or a reader whose lookup answered "no document" or failed, sends no field at all — never
 * a null, and never a version taken from anywhere but the selection the link was built from. The api
 * records nothing for such a booking rather than today's version, which is the honest record.
 *
 * The reader itself refuses to answer for a selection that moved since the link rendered (it throws
 * "Checkout document selection changed"), so what reaches the api is the identity that was on screen.
 */
export function acceptedAdvertisingTerms(read: CheckoutDocumentRead | undefined): {advertisingTerms?: ContractDocumentIdentity} {
	const {contractDocument} = read?.() ?? {};
	return contractDocument ? {advertisingTerms: contractDocument} : {};
}

/** Where the acceptance sentence's link goes, and whether it is the frozen document or the mutable page. */
export interface AdvertisingTermsLinkTarget {
	readonly href: string;
	/** True when `href` is the registered document the api announced; false when it is `/advertising-terms`. */
	readonly immutable: boolean;
}

/**
 * The link beside the acceptance box: the immutable registered document when the api announced one, so
 * the hash the submission echoes describes exactly the bytes the reader can open; otherwise the page,
 * which is what a brand with no registered document, or an api that could not be asked, can offer.
 */
export function advertisingTermsLink(document: LinkedContractDocument | null): AdvertisingTermsLinkTarget {
	return document
		? {href: document.href, immutable: true}
		: {href: ADVERTISING_TERMS_PATH, immutable: false};
}
